import { useCallback, useRef, useSyncExternalStore } from 'react'
import { createSnapshotConsumer } from '@highlight/ipc'
import { useGlobalState, useSnapshotConsumer } from '@highlight/ipc/react'
import * as Sentry from '@sentry/electron/renderer'
import {
  EMPTY_STREAMING_SUMMARY_SNAPSHOT,
  STREAMING_SUMMARY_TOPIC,
  type StreamingSummarySnapshot,
} from '@shared/streaming-summary'

const streamingSummaryConsumer = createSnapshotConsumer<StreamingSummarySnapshot>({
  topic: STREAMING_SUMMARY_TOPIC,
  initialSnapshot: EMPTY_STREAMING_SUMMARY_SNAPSHOT,
  onError: (error) => {
    Sentry.captureException(error)
  },
})

function useElectronStreamingSummary(): string {
  return useSnapshotConsumer(streamingSummaryConsumer).text
}

/** The browser runtime has no main process to serve the snapshot feed; its
 *  shims still keep streamingSummary on the emulated 'audio-note-state' key. */
function useBrowserStreamingSummary(): string {
  const [state] = useGlobalState<{ streamingSummary?: string } | undefined>('audio-note-state', undefined)
  return state?.streamingSummary ?? ''
}

/**
 * In-progress AI summary text for the active note, delivered over the
 * streaming-summary snapshot feed instead of the shared audio-note state
 * (see @shared/streaming-summary). Subscribes only while mounted and replays
 * the latest text to late mounts, including after the stream completes.
 */
export const useStreamingSummaryText: () => string = __IS_BROWSER__
  ? useBrowserStreamingSummary
  : useElectronStreamingSummary

function useElectronHasStreamingSummary(): boolean {
  return useSyncExternalStore(
    streamingSummaryConsumer.subscribe,
    () => streamingSummaryConsumer.getSnapshot().text.trim().length > 0,
  )
}

function useBrowserHasStreamingSummary(): boolean {
  return useBrowserStreamingSummary().trim().length > 0
}

/**
 * Whether the stream has produced any text yet — a boolean that flips once,
 * for consumers deriving a phase rather than rendering the text. Subscribing to
 * the text there would drag them into the per-token render loop.
 */
export const useHasStreamingSummaryText: () => boolean = __IS_BROWSER__
  ? useBrowserHasStreamingSummary
  : useElectronHasStreamingSummary

function useElectronReadStreamingSummary(): () => string {
  return useCallback(() => streamingSummaryConsumer.getSnapshot().text, [])
}

function useBrowserReadStreamingSummary(): () => string {
  const text = useBrowserStreamingSummary()
  const latest = useRef(text)
  latest.current = text
  return useCallback(() => latest.current, [])
}

/**
 * Read the streamed text without subscribing to it, for consumers that already
 * re-render on some other signal and just need the text as it stands at that
 * moment. Only current while something else holds the feed open — pair it with
 * `useHasStreamingSummaryText`, which does.
 */
export const useReadStreamingSummaryText: () => () => string = __IS_BROWSER__
  ? useBrowserReadStreamingSummary
  : useElectronReadStreamingSummary
